import _defineProperty from "/app/workspaces/v_1/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
//  @ts-check

const DEFAULT_DEBOUNCE_TIMEOUT = 20;
export const EventType = Object.freeze({
  FILE_ADDED: 'file-added',
  FILE_REMOVED: 'file-removed',
  FILE_UPLOAD_START: 'file-upload-start',
  FILE_UPLOAD_PROGRESS: 'file-upload-progress',
  FILE_UPLOAD_SUCCESS: 'file-upload-success',
  FILE_UPLOAD_FAILED: 'file-upload-failed',
  FILE_URL_CHANGED: 'file-url-changed',
  MODAL_OPEN: 'modal-open',
  MODAL_CLOSE: 'modal-close',
  DONE_CLICK: 'done-click',
  UPLOAD_CLICK: 'upload-click',
  ACTIVITY_CHANGE: 'activity-change',
  COMMON_UPLOAD_START: 'common-upload-start',
  COMMON_UPLOAD_PROGRESS: 'common-upload-progress',
  COMMON_UPLOAD_SUCCESS: 'common-upload-success',
  COMMON_UPLOAD_FAILED: 'common-upload-failed',
  CHANGE: 'change',
  GROUP_CREATED: 'group-created'
});

/**
 * @typedef {{
 *   [EventType.FILE_ADDED]: import('../../index.js').OutputFileEntry<'idle'>;
 *   [EventType.FILE_REMOVED]: import('../../index.js').OutputFileEntry<'removed'>;
 *   [EventType.FILE_UPLOAD_START]: import('../../index.js').OutputFileEntry<'uploading'>;
 *   [EventType.FILE_UPLOAD_PROGRESS]: import('../../index.js').OutputFileEntry<'uploading'>;
 *   [EventType.FILE_UPLOAD_SUCCESS]: import('../../index.js').OutputFileEntry<'success'>;
 *   [EventType.FILE_UPLOAD_FAILED]: import('../../index.js').OutputFileEntry<'failed'>;
 *   [EventType.FILE_URL_CHANGED]: import('../../index.js').OutputFileEntry<'success'>;
 *   [EventType.MODAL_OPEN]: void;
 *   [EventType.MODAL_CLOSE]: void;
 *   [EventType.ACTIVITY_CHANGE]: {
 *     activity: import('../../abstract/ActivityBlock.js').ActivityType;
 *   };
 *   [EventType.UPLOAD_CLICK]: void;
 *   [EventType.DONE_CLICK]: import('../../index.js').OutputCollectionState;
 *   [EventType.COMMON_UPLOAD_START]: import('../../index.js').OutputCollectionState<'uploading'>;
 *   [EventType.COMMON_UPLOAD_PROGRESS]: import('../../index.js').OutputCollectionState<'uploading'>;
 *   [EventType.COMMON_UPLOAD_SUCCESS]: import('../../index.js').OutputCollectionState<'success'>;
 *   [EventType.COMMON_UPLOAD_FAILED]: import('../../index.js').OutputCollectionState<'failed'>;
 *   [EventType.CHANGE]: import('../../index.js').OutputCollectionState;
 *   [EventType.GROUP_CREATED]: import('../../index.js').OutputCollectionState<'success', 'has-group'>;
 * }} EventPayload
 */

export class EventEmitter {
  /** @param {(...args: unknown[]) => void} debugPrint */
  constructor(debugPrint) {
    /**
     * @private
     * @type {Map<string, number>}
     */
    _defineProperty(this, "_timeoutStore", new Map());
    /**
     * @private
     * @type {Set<import('../../abstract/Block.js').Block>}
     */
    _defineProperty(this, "_targets", new Set());
    /**
     * @private
     * @type {((...args: unknown[]) => void) | null}
     */
    _defineProperty(this, "_debugPrint", null);
    this._debugPrint = debugPrint;
  }

  /** @param {import('../../abstract/Block.js').Block} target */
  bindTarget(target) {
    this._targets.add(target);
  }

  /** @param {import('../../abstract/Block.js').Block} target */
  unbindTarget(target) {
    this._targets.delete(target);
  }

  /**
   * @private
   * @template {(typeof EventType)[keyof typeof EventType]} T
   * @param {T} type
   * @param {unknown} [payload]
   */
  _dispatch(type, payload) {
    var _this$_debugPrint;
    for (const target of this._targets) {
      target.dispatchEvent(new CustomEvent(type, {
        detail: payload
      }));
    }
    (_this$_debugPrint = this._debugPrint) === null || _this$_debugPrint === void 0 || _this$_debugPrint.call(this, () => {
      const copyPayload = !!payload && typeof payload === 'object' ? _objectSpread({}, payload) : payload;
      return ["event \"".concat(type, "\""), copyPayload];
    });
  }

  /**
   * @template {(typeof EventType)[keyof typeof EventType]} T
   * @template {boolean | number | undefined} TDebounce
   * @param {T} type
   * @param {TDebounce extends false | undefined ? unknown : () => unknown} [payload]
   * @param {{ debounce?: TDebounce }} [options]
   */
  emit(type, payload) {
    let {
      debounce
    } = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    if (typeof debounce !== 'number' && !debounce) {
      this._dispatch(type, typeof payload === 'function' ? payload() : payload);
      return;
    }
    if (this._timeoutStore.has(type)) {
      window.clearTimeout(this._timeoutStore.get(type));
    }
    const timeout = typeof debounce === 'number' ? debounce : DEFAULT_DEBOUNCE_TIMEOUT;
    const timeoutId = window.setTimeout(() => {
      this._dispatch(type, typeof payload === 'function' ? payload() : payload);
      this._timeoutStore.delete(type);
    }, timeout);
    this._timeoutStore.set(type, timeoutId);
  }
}